import { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/layout/Layout";
import { Skeleton } from "@/components/ui/skeleton";
import { useEditableCaseCategories } from "@/hooks/useEditableCaseCategories";

function CaseCard({
  article,
  index,
  getCategoryName,
}: {
  article: any;
  index: number;
  getCategoryName: (id: string) => string;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const isInView = useInView(linkRef, { once: true, margin: "-100px" });

  // Parallax: image moves slower than scroll
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  // Alternate between large (span-2) and regular cards for editorial flow
  const isLarge = index % 5 === 0;

  return (
    <motion.div
      ref={cardRef}
      className={isLarge ? "md:col-span-2" : ""}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{
        duration: 0.7,
        delay: (index % 3) * 0.12,
        ease: [0.25, 0.1, 0.25, 1],
      }}
    >
      <Link
        ref={linkRef}
        to={`/cases/${article.slug}`}
        className="group block"
      >
        <div
          className={`relative overflow-hidden ${
            isLarge ? "aspect-[16/9]" : "aspect-[3/4]"
          }`}
        >
          {article.hero_image_url ? (
            <motion.img
              src={article.hero_image_url}
              alt={article.project_name}
              className="absolute inset-0 h-[116%] w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.04]"
              style={{ y: imgY }}
              loading="lazy"
            />
          ) : (
            <div className="flex h-full items-center justify-center bg-muted text-muted-foreground text-sm font-sans">
              No image
            </div>
          )}

          {/* Subtle gradient overlay on hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
        </div>

        <div className="mt-5 space-y-2">
          <h2 className="font-serif text-lg md:text-xl text-foreground leading-snug transition-colors duration-300 group-hover:text-primary">
            {article.project_name}
          </h2>

          <div className="flex items-center gap-3 font-sans text-xs uppercase tracking-[0.15em] text-muted-foreground">
            {article.client_name && <span>{article.client_name}</span>}
            {article.client_name && article.location && (
              <span className="text-border">·</span>
            )}
            {article.location && <span>{article.location}</span>}
          </div>

          {article.category && article.category !== "uncategorized" && (
            <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-muted-foreground/60">
              {getCategoryName(article.category)}
            </p>
          )}
        </div>
      </Link>
    </motion.div>
  );
}

export default function Cases() {
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const { categories, getCategoryName } = useEditableCaseCategories();
  const headerRef = useRef<HTMLDivElement>(null);
  const headerInView = useInView(headerRef, { once: true });

  const { data: articles, isLoading } = useQuery({
    queryKey: ["published-case-articles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("case_articles")
        .select(
          "id, slug, project_name, client_name, industry, location, hero_image_url, tag_material_type, tag_spatial_type, category, display_order"
        )
        .eq("published", true)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const filtered = (articles || []).filter(
    (a) => categoryFilter === "all" || (a as any).category === categoryFilter
  );

  const activeCats = categories.filter((cat) =>
    (articles || []).some((a) => (a as any).category === cat.id)
  );

  return (
    <Layout>
      <main className="min-h-screen bg-background">
        {/* Hero header */}
        <div
          ref={headerRef}
          className="pt-32 pb-16 md:pt-40 md:pb-20 px-6 md:px-16"
        >
          <div className="mx-auto max-w-[1400px]">
            <motion.h1
              className="font-serif text-4xl md:text-6xl lg:text-7xl text-foreground tracking-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={headerInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            >
              Solutions
            </motion.h1>
            <motion.p
              className="mt-4 max-w-lg font-sans text-sm md:text-base text-muted-foreground leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={headerInView ? { opacity: 1, y: 0 } : {}}
              transition={{
                duration: 0.8,
                delay: 0.15,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              Completed projects showcasing our material and design approach.
            </motion.p>

            {/* Category Filter */}
            {activeCats.length > 0 && (
              <motion.div
                className="mt-10 flex flex-wrap items-center gap-1"
                initial={{ opacity: 0 }}
                animate={headerInView ? { opacity: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <button
                  className={`px-4 py-2 font-sans text-xs uppercase tracking-[0.15em] transition-all duration-300 ${
                    categoryFilter === "all"
                      ? "text-foreground border-b border-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  onClick={() => setCategoryFilter("all")}
                >
                  All
                </button>
                {activeCats.map((cat) => (
                  <button
                    key={cat.id}
                    className={`px-4 py-2 font-sans text-xs uppercase tracking-[0.15em] transition-all duration-300 ${
                      categoryFilter === cat.id
                        ? "text-foreground border-b border-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    onClick={() => setCategoryFilter(cat.id)}
                  >
                    {cat.name}
                  </button>
                ))}
              </motion.div>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="mx-auto max-w-[1400px] px-6 md:px-16">
          <div className="h-px bg-border" />
        </div>

        {/* Cases Grid */}
        <div className="px-6 md:px-16 py-16 md:py-24">
          <div className="mx-auto max-w-[1400px]">
            {isLoading ? (
              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton
                    key={i}
                    className={`rounded-none ${
                      i === 0 ? "aspect-[16/9] md:col-span-2" : "aspect-[3/4]"
                    }`}
                  />
                ))}
              </div>
            ) : !filtered.length ? (
              <p className="text-muted-foreground font-sans text-sm">
                No published cases yet.
              </p>
            ) : (
              <div className="grid gap-x-6 gap-y-16 md:grid-cols-2 lg:grid-cols-3">
                {filtered.map((article, i) => (
                  <CaseCard
                    key={article.id}
                    article={article}
                    index={i}
                    getCategoryName={getCategoryName}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </Layout>
  );
}
